import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar,
  faFileAlt,
  faBook,
  faBlog,
  faUsers,
  faBookOpen,
  faChartLine,
  faCog,
  faUser,
  faInfoCircle,
  faLightbulb,
  faExclamationTriangle,
  faBars,
  faTimes,
  faLink,
  faCheck,
  faChevronDown,
  faCopy,
  faHome
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { teacherDocsData } from './TeacherDocsData';
import { motion, AnimatePresence } from 'framer-motion';
import VideoViewerV2 from '../Shared/VideoViewerV2';
import teacherDocVideo from '../../assets/images/documentation/teacher_Doc.mp4';

const iconMap = {
  faChartBar,
  faFileAlt,
  faBook,
  faBlog,
  faUsers,
  faBookOpen,
  faChartLine,
  faCog,
  faUser,
};

const getNoteStyles = (type, lang) => {
  switch (type) {
    case 'success':
      return { className: 'alert-success', icon: faLightbulb, label: lang === 'en' ? 'Tip' : 'تلميح' };
    case 'warning':
      return { className: 'alert-warning', icon: faExclamationTriangle, label: lang === 'en' ? 'Important' : 'هام' };
    case 'danger':
      return { className: 'alert-danger', icon: faExclamationTriangle, label: lang === 'en' ? 'Warning' : 'تحذير' };
    case 'info':
    default:
      return { className: 'alert-info', icon: faInfoCircle, label: lang === 'en' ? 'Note' : 'ملاحظة' };
  }
};

const UI_STRINGS = {
  ar: {
    sidebarTitle: "دليل المنصة",
    sidebarSubtitle: "دليل المعلم الشامل",
    badge: "الإصدار المطور",
    heroTitle: "دليل استخدام ",
    heroGradient: "منصة المعلم",
    heroDesc: "مرحباً بك في منصة امتحاني. صُمم هذا الدليل الشامل ليرافقك خطوة بخطوة في احتراف أدوات لوحة التحكم وتقديم أفضل تجربة تعليمية لطلابك.",
    copyLink: "نسخ رابط القسم",
    copyContent: "نسخ المحتوى",
    copyStepContent: "نسخ محتوى الخطوة",
    copyStepLink: "نسخ رابط هذه الخطوة",
    notePrefix: "ملاحظة: ",
    altImg: "توضيح الخطوة",
    backToHome: "العودة للرئيسية"
  },
  en: {
    sidebarTitle: "Platform Guide",
    sidebarSubtitle: "Teacher Comprehensive Guide",
    badge: "Upgraded Version",
    heroTitle: "How to use ",
    heroGradient: "Teacher Platform",
    heroDesc: "Welcome to EMt7any Platform. This comprehensive guide is designed to accompany you step by step in mastering dashboard tools and providing the best educational experience for your students.",
    copyLink: "Copy Section Link",
    copyContent: "Copy Content",
    copyStepContent: "Copy Step Content",
    copyStepLink: "Copy Step Link",
    notePrefix: "Note: ",
    altImg: "Step Illustration",
    backToHome: "Back to Home"
  }
};

const TeacherDocumentation = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('lang') === 'en' ? 'en' : 'ar';
  });
  const [activeMainSection, setActiveMainSection] = useState("");
  const [activeSubSection, setActiveSubSection] = useState("");
  const [expandedSection, setExpandedSection] = useState("dashboard");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [copiedSection, setCopiedSection] = useState(null);
  const [copiedContentId, setCopiedContentId] = useState(null);

  const t = UI_STRINGS[lang];
  const currentDocsData = teacherDocsData[lang];

  const handleLanguageToggle = () => {
    const newLang = lang === 'en' ? 'ar' : 'en';
    setLang(newLang);
    const url = new URL(window.location);
    url.searchParams.set('lang', newLang);
    window.history.pushState(null, null, url.toString());
  };

  const observerRef = useRef(null);
  const isScrolling = useRef(false);
  const scrollTimeout = useRef(null);

  const handleCopyLink = (id) => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    navigator.clipboard.writeText(url);
    setCopiedSection(id);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const handleCopySectionContent = (id, title, description) => {
    const content = `${title}\n\n${description}`;
    navigator.clipboard.writeText(content);
    setCopiedContentId(id);
    setTimeout(() => setCopiedContentId(null), 2000);
  };

  const handleCopyActionContent = (id, title, steps, warning) => {
    let content = `${title}\n\n`;
    steps.forEach(step => {
      if (!step.startsWith('[IMG]')) {
        content += `- ${step}\n`;
      }
    });
    if (warning) {
      content += `\n${t.notePrefix}${warning}`;
    }
    navigator.clipboard.writeText(content);
    setCopiedContentId(id);
    setTimeout(() => setCopiedContentId(null), 2000);
  };

  useEffect(() => {
    // Initial scroll on load
    if (window.location.hash) {
      const id = window.location.hash.substring(1);
      isScrolling.current = true; // Prevent scroll spy from overriding initially

      // Try scrolling multiple times to account for layout shifts from loading images
      setTimeout(() => scrollToSection(id, false), 100);
      setTimeout(() => scrollToSection(id, false), 500);
      setTimeout(() => scrollToSection(id, false), 1200);
    }
  }, []);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    const options = {
      root: null,
      rootMargin: '-15% 0px -65% 0px', // Center-ish detection
      threshold: 0
    };

    observerRef.current = new IntersectionObserver((entries) => {
      if (isScrolling.current) return; // Prevent scroll spy from overriding manual clicks

      // Find the most visible intersecting entry
      const visibleEntries = entries.filter(e => e.isIntersecting);
      if (visibleEntries.length > 0) {
        // Sort to find the one closest to top
        visibleEntries.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const topEntry = visibleEntries[0];
        const id = topEntry.target.id;

        // Update URL automatically on scroll
        if (window.location.hash !== `#${id}`) {
          const params = new URLSearchParams(window.location.search);
          const langParam = params.get('lang') ? `?lang=${params.get('lang')}` : '';
          window.history.replaceState(null, null, `${langParam}#${id}`);
        }

        // Determine if it's a main section or sub-section
        if (id.includes('-action-')) {
          setActiveSubSection(id);
          const mainId = id.split('-action-')[0];
          setActiveMainSection(mainId);
          setExpandedSection(mainId); // Auto-expand sidebar
        } else {
          setActiveMainSection(id);
          setExpandedSection(id);
        }
      }
    }, options);

    // Observe all main sections and sub-sections
    currentDocsData.forEach((doc) => {
      const mainEl = document.getElementById(doc.id);
      if (mainEl) observerRef.current.observe(mainEl);

      doc.actions.forEach((_, actIndex) => {
        const subId = `${doc.id}-action-${actIndex + 1}`;
        const subEl = document.getElementById(subId);
        if (subEl) observerRef.current.observe(subEl);
      });
    });

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [lang]);

  const scrollToSection = (id, closeSidebar = true) => {
    isScrolling.current = true;
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

    // Allow time for smooth scroll to finish before re-enabling scroll spy
    scrollTimeout.current = setTimeout(() => {
      isScrolling.current = false;
    }, 1000);

    const element = document.getElementById(id);
    if (element) {
      const yOffset = -80; // Header offset
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });

      // Update URL
      if (window.location.hash !== `#${id}`) {
        const params = new URLSearchParams(window.location.search);
        const langParam = params.get('lang') ? `?lang=${params.get('lang')}` : '';
        window.history.pushState(null, null, `${langParam}#${id}`);
      }

      // Update state immediately for better UX
      if (id.includes('-action-')) {
        setActiveSubSection(id);
        const mainId = id.split('-action-')[0];
        setActiveMainSection(mainId);
        setExpandedSection(mainId);
      } else {
        setActiveMainSection(id);
        setExpandedSection(id);
      }

      if (closeSidebar) {
        setIsSidebarOpen(false); // Close sidebar on mobile
      }
    }
  };

  const toggleSidebarAccordion = (id) => {
    setExpandedSection(prev => prev === id ? null : id);
  };

  return (
    <div className={`docs-layout lang-${lang}`} dir={lang === 'en' ? 'ltr' : 'rtl'}>

      {/* Mobile Sidebar Toggle */}
      <button
        className="mobile-toggle-btn"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        <FontAwesomeIcon icon={isSidebarOpen ? faTimes : faBars} />
      </button>

      {/* Overlay for mobile */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="sidebar-overlay"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar Navigation */}
      <aside className={`docs-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-content">
          <div className="sidebar-header">
            <img src="/assets/images/new_log_design.svg" alt="EMt7any Logo" className="sidebar-logo" />
            <div className="sidebar-titles">
              <h2 className="sidebar-title">{t.sidebarTitle}</h2>
              <div className="sidebar-subtitle">{t.sidebarSubtitle}</div>
            </div>
          </div>

          <div className="sidebar-nav">
            {currentDocsData.map(doc => (
              <div key={doc.id} className="nav-group">
                <button
                  onClick={() => {
                    toggleSidebarAccordion(doc.id);
                    scrollToSection(doc.id, false);
                  }}
                  className={`nav-group-btn ${activeMainSection === doc.id ? 'active' : ''} ${expandedSection === doc.id ? 'expanded' : ''}`}
                >
                  <div className="nav-group-content">
                    <FontAwesomeIcon icon={iconMap[doc.icon]} className="nav-icon" />
                    <span>{doc.title.split(' (')[0]}</span> {/* Show only Arabic part */}
                  </div>
                  <motion.div
                    animate={{ rotate: expandedSection === doc.id ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <FontAwesomeIcon icon={faChevronDown} className="nav-chevron" />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {expandedSection === doc.id && (
                    <motion.ul
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="nav-sublist"
                    >
                      {doc.actions.map((action, actIndex) => {
                        const subId = `${doc.id}-action-${actIndex + 1}`;
                        return (
                          <li key={subId}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                scrollToSection(subId, true);
                              }}
                              className={`nav-sub-btn ${activeSubSection === subId ? 'active' : ''}`}
                              title={action.title}
                            >
                              <span className="nav-sub-text">{action.title}</span>
                            </button>
                          </li>
                        );
                      })}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          <div className="sidebar-footer">
            <button className="back-home-btn" onClick={() => navigate('/')}>
              <FontAwesomeIcon icon={faHome} />
              <span>{t.backToHome}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="docs-main">
        <div className="docs-hero">
          <div className="docs-hero-bg"></div>
          <div className="hero-content">
            <button className="lang-toggle-btn" onClick={handleLanguageToggle}>
              {lang === 'en' ? 'عربي' : 'English'}
            </button>
            <div className="hero-badge">
              <span className="badge-icon">✨</span>
              <span className="badge-text">{t.badge}</span>
            </div>
            <h1 className="docs-main-title">
              {t.heroTitle} <span className="text-gradient">{t.heroGradient}</span>
            </h1>
            <p className="docs-main-desc">
              {t.heroDesc}
            </p>
            <div className="hero-video-wrapper" style={{ width: '100%', marginTop: '32px', zIndex: 5 }}>
              <VideoViewerV2
                url={teacherDocVideo}
                title={lang === 'en' ? "Teacher Platform Guide Video" : "فيديو شرح دليل المعلم"}
                preload="metadata"
                colorScheme="dark"
              />
            </div>
          </div>
          <div className="hero-decoration">
            <div className="glass-shape shape-blue"></div>
            <div className="glass-shape shape-purple"></div>
          </div>
        </div>

        <div className="docs-sections-container">
          {currentDocsData.map((doc) => (
            <section id={doc.id} key={doc.id} className="doc-section-block">
              <div className="section-card">
                <div className="section-card-header">
                  <div className="section-heading">
                    <div className="section-icon-box">
                      <FontAwesomeIcon icon={iconMap[doc.icon]} />
                    </div>
                    <h2>{doc.title}</h2>
                  </div>
                  <div className="section-btn-group">
                    <button
                      className={`copy-link-btn ${copiedContentId === doc.id ? 'copied' : ''}`}
                      onClick={() => handleCopySectionContent(doc.id, doc.title, doc.description)}
                      title={t.copyContent}
                    >
                      <FontAwesomeIcon icon={copiedContentId === doc.id ? faCheck : faCopy} />
                    </button>
                    <button
                      className={`copy-link-btn ${copiedSection === doc.id ? 'copied' : ''}`}
                      onClick={() => handleCopyLink(doc.id)}
                      title={t.copyLink}
                    >
                      <FontAwesomeIcon icon={copiedSection === doc.id ? faCheck : faLink} />
                    </button>
                  </div>
                </div>

                <p className="section-desc">{doc.description}</p>

                {/* Cover Image */}
                {doc.screenshotId && (
                  <div className="image-cover-wrapper">
                    <img
                      src={doc.screenshotId.startsWith('/') || doc.screenshotId.startsWith('data:') || doc.screenshotId.startsWith('http')
                        ? doc.screenshotId
                        : `/src/assets/images/documentation/${doc.screenshotId}`}
                      alt={doc.title}
                      className="doc-cover-image zoomable-image"
                      onClick={(e) => setZoomedImage(e.target.src)}
                    />
                  </div>
                )}
              </div>

              <div className="actions-timeline">
                {doc.actions.map((action, actIndex) => {
                  const note = getNoteStyles(action.type, lang);
                  const subId = `${doc.id}-action-${actIndex + 1}`;

                  return (
                    <div id={subId} key={subId} className={`action-block ${activeSubSection === subId ? 'highlighted' : ''}`}>
                      <div className="action-card">
                        <div className="action-card-header">
                          <h3 className="action-title">{action.title}</h3>
                          <div className="section-btn-group sub-btn-group">
                            <button
                              className={`copy-link-btn sub-copy-btn ${copiedContentId === subId ? 'copied' : ''}`}
                              onClick={() => handleCopyActionContent(subId, action.title, action.steps, action.warning)}
                              title={t.copyStepContent}
                            >
                              <FontAwesomeIcon icon={copiedContentId === subId ? faCheck : faCopy} />
                            </button>
                            <button
                              className={`copy-link-btn sub-copy-btn ${copiedSection === subId ? 'copied' : ''}`}
                              onClick={() => handleCopyLink(subId)}
                              title={t.copyStepLink}
                            >
                              <FontAwesomeIcon icon={copiedSection === subId ? faCheck : faLink} />
                            </button>
                          </div>
                        </div>

                        <div className="action-steps-wrapper">
                          {action.steps.map((step, stepIndex) => {
                            if (step.startsWith('[IMG]')) {
                              const imgSrc = step.replace('[IMG]', '');
                              return (
                                <div key={stepIndex} className="step-image-item">
                                  <img
                                    src={imgSrc}
                                    alt={t.altImg}
                                    className="step-inline-image zoomable-image"
                                    onClick={() => setZoomedImage(imgSrc)}
                                  />
                                </div>
                              );
                            }
                            return (
                              <div key={stepIndex} className="step-text-item">
                                <span className="step-content">{step}</span>
                              </div>
                            );
                          })}
                        </div>

                        {action.warning && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`alert-box ${note.className}`}
                          >
                            <div className="alert-header">
                              <FontAwesomeIcon icon={note.icon} className="alert-icon" />
                              <span>{note.label}</span>
                            </div>
                            <p className="alert-content">{action.warning}</p>
                          </motion.div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </main>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {zoomedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lightbox-overlay"
            onClick={() => setZoomedImage(null)}
          >
            <button className="lightbox-close" onClick={() => setZoomedImage(null)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              src={zoomedImage}
              className="lightbox-image"
              alt="Zoomed"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Zain:wght@400;700;800;900&display=swap');

        .docs-layout {
          display: flex;
          min-height: 100vh;
          background-color: #F8FAFC;
          font-family: 'Zain', sans-serif;
          color: #1E293B;
        }

        /* Sidebar Styling */
        .docs-sidebar {
          width: 280px;
          flex-shrink: 0;
          background: #FFFFFF;
          border-left: 1px solid #E2E8F0;
          height: 100vh;
          position: sticky;
          top: 0;
          overflow-y: auto;
          z-index: 40;
        }

        /* Customize Scrollbar for Sidebar */
        .docs-sidebar::-webkit-scrollbar { width: 5px; }
        .docs-sidebar::-webkit-scrollbar-track { background: transparent; }
        .docs-sidebar::-webkit-scrollbar-thumb { background-color: #CBD5E1; border-radius: 10px; }

        .sidebar-content {
          display: flex;
          flex-direction: column;
        }

        .sidebar-header {
          padding: 24px 20px 16px;
          background: #FFFFFF;
          position: sticky;
          top: 0;
          z-index: 10;
          border-bottom: 1px solid #E2E8F0;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .sidebar-logo {
          width: 40px;
          height: auto;
          flex-shrink: 0;
        }

        .sidebar-titles {
          display: flex;
          flex-direction: column;
        }

        .sidebar-title {
          font-size: 1.5rem;
          font-weight: 800;
          color: #0F172A;
          margin: 0;
          line-height: 1.2;
        }

        .sidebar-subtitle {
          color: #64748B;
          font-size: 0.9rem;
          font-weight: 600;
          margin-top: 4px;
        }

        .sidebar-nav {
          padding: 12px 16px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex-grow: 1;
        }

        .sidebar-footer {
          padding: 16px 20px;
          border-top: 1px solid #E2E8F0;
          background: #F8FAFC;
        }

        .back-home-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 12px;
          background: #EEF2FF;
          color: #4F46E5;
          border: 1px solid #C7D2FE;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .back-home-btn:hover {
          background: #4F46E5;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.2);
        }

        .nav-group {
          display: flex;
          flex-direction: column;
        }

        .nav-group-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 14px;
          background: transparent;
          border: none;
          border-radius: 8px;
          color: #475569;
          font-family: inherit;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .nav-group-btn:hover {
          color: #0F172A;
          background: #F8FAFC;
        }

        .nav-group-btn.expanded {
          color: #0F172A;
        }

        .nav-group-btn.active {
          background: #EEF2FF;
          color: #4F46E5;
          font-weight: 700;
        }

        .nav-group-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .nav-icon {
          width: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94A3B8;
          font-size: 1.1rem;
          transition: all 0.2s ease;
        }

        .nav-group-btn:hover .nav-icon, .nav-group-btn.expanded .nav-icon {
          color: #64748B;
        }

        .nav-group-btn.active .nav-icon {
          color: #4F46E5;
        }

        .nav-chevron {
          font-size: 0.75rem;
          color: #CBD5E1;
        }

        .nav-sublist {
          list-style: none;
          padding: 4px 12px 4px 0;
          margin: 0 22px 0 0;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          gap: 2px;
          border-right: 1px solid #E2E8F0;
        }

        .nav-sub-btn {
          width: 100%;
          text-align: right;
          padding: 8px 12px;
          background: transparent;
          border: none;
          border-radius: 6px;
          color: #64748B;
          font-family: inherit;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: block;
          position: relative;
        }

        .nav-sub-btn:hover {
          color: #0F172A;
          background: #F8FAFC;
        }

        .nav-sub-btn.active {
          color: #4F46E5;
          font-weight: 700;
        }

        .nav-sub-text {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Main Content */
        .docs-main {
          flex: 1;
          padding: 40px;
          max-width: 1100px;
          margin: 0 auto;
        }

        .docs-hero {
          background: #FFFFFF;
          border-radius: 24px;
          padding: 60px 50px;
          margin-bottom: 50px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03);
          border: 1px solid #E2E8F0;
        }

        .docs-hero-bg {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: radial-gradient(#E2E8F0 1px, transparent 1px);
          background-size: 20px 20px;
          opacity: 0.5;
          z-index: 1;
        }

        .hero-content {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: #EEF2FF;
          border: 1px solid #C7D2FE;
          border-radius: 20px;
          color: #4F46E5;
          font-weight: 700;
          font-size: 0.9rem;
          margin-bottom: 20px;
        }

        .lang-toggle-btn {
          position: absolute;
          top: 0;
          inset-inline-end: 0;
          background: #0F172A;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 30px;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
        }
        
        .lang-toggle-btn:hover {
          background: #1E293B;
          transform: translateY(-2px);
        }

        .docs-main-title {
          font-size: 3.5rem;
          font-weight: 900;
          margin: 0 0 16px 0;
          color: #0F172A;
          line-height: 1.2;
        }

        .text-gradient {
          background: linear-gradient(135deg, #4F46E5 0%, #3B82F6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .docs-main-desc {
          font-size: 1.25rem;
          font-weight: 600;
          line-height: 1.6;
          max-width: 700px;
          margin: 0;
          color: #475569;
        }

        .hero-decoration {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 1;
          pointer-events: none;
        }

        .glass-shape {
          position: absolute;
          border-radius: 50%;
          filter: blur(60px);
          opacity: 0.5;
        }

        .shape-blue {
          width: 300px;
          height: 300px;
          background: #3B82F6;
          top: -100px;
          left: -50px;
        }

        .shape-purple {
          width: 250px;
          height: 250px;
          background: #8B5CF6;
          bottom: -80px;
          left: 20%;
        }

        .docs-sections-container {
          display: flex;
          flex-direction: column;
          gap: 60px;
        }

        .doc-section-block {
          scroll-margin-top: 40px;
        }

        .section-card {
          background: white;
          border-radius: 24px;
          padding: 40px;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03);
          border: 1px solid #F1F5F9;
          margin-bottom: 30px;
        }

        .section-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .section-heading {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .section-icon-box {
          width: 48px;
          height: 48px;
          background: #EEF2FF;
          color: #4F46E5;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
        }

        .section-heading h2 {
          font-size: 2rem;
          font-weight: 800;
          color: #0F172A;
          margin: 0;
        }

        .section-btn-group {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .copy-link-btn {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #F8FAFC;
          border: 1px solid #E2E8F0;
          color: #64748B;
          border-radius: 10px;
          cursor: pointer;
          font-size: 1rem;
          transition: all 0.2s;
        }

        .copy-link-btn:hover {
          background: #F1F5F9;
          color: #0F172A;
        }

        .copy-link-btn.copied {
          background: #10B981;
          border-color: #10B981;
          color: white;
        }

        .section-desc {
          font-size: 1.15rem;
          color: #475569;
          line-height: 1.7;
          font-weight: 600;
          margin-bottom: 30px;
        }

        .image-cover-wrapper {
          width: 100%;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid #E2E8F0;
          box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1);
        }

        .doc-cover-image {
          width: 100%;
          height: auto;
          display: block;
          object-fit: cover;
        }

        /* Actions Timeline */
        .actions-timeline {
          padding-right: 20px;
          border-right: 3px dashed #E2E8F0;
          display: flex;
          flex-direction: column;
          gap: 30px;
          margin-right: 20px;
        }

        .action-block {
          position: relative;
          scroll-margin-top: 80px;
        }

        .action-block::before {
          content: '';
          position: absolute;
          right: -29px; /* adjust for border and padding */
          top: 30px;
          width: 14px;
          height: 14px;
          background: white;
          border: 3px solid #CBD5E1;
          border-radius: 50%;
          z-index: 2;
          transition: all 0.3s;
        }

        .action-block.highlighted::before {
          border-color: #4F46E5;
          background: #EEF2FF;
          box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.2);
        }

        .action-card {
          background: white;
          border-radius: 20px;
          padding: 30px;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
          border: 1px solid #F1F5F9;
          transition: all 0.3s ease;
        }
        
        .action-block.highlighted .action-card {
          border-color: #C7D2FE;
          box-shadow: 0 10px 25px -5px rgba(79, 70, 229, 0.1);
        }

        .action-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .action-title {
          font-size: 1.4rem;
          font-weight: 800;
          color: #0F172A;
          margin: 0;
        }

        .sub-btn-group {
          opacity: 0;
          transform: scale(0.95);
          transition: all 0.2s ease;
        }
        
        .action-card:hover .sub-btn-group {
          opacity: 1;
          transform: scale(1);
        }

        .sub-copy-btn {
          width: 32px;
          height: 32px;
          font-size: 0.85rem;
        }
        
        .sub-btn-group.copied {
          opacity: 1;
        }

        .action-steps-wrapper {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .step-text-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          font-size: 1.1rem;
          color: #334155;
          font-weight: 600;
          line-height: 1.6;
        }

        .step-image-item {
          margin: 10px 0;
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid #E2E8F0;
        }

        .step-inline-image {
          width: 100%;
          height: auto;
          display: block;
        }

        .zoomable-image {
          cursor: zoom-in;
          transition: transform 0.3s ease;
        }

        .zoomable-image:hover {
          transform: scale(1.02);
        }

        /* Alerts */
        .alert-box {
          margin-top: 24px;
          padding: 20px;
          border-radius: 12px;
          border-right: 4px solid transparent;
        }

        .alert-header {
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 800;
          font-size: 1.1rem;
          margin-bottom: 8px;
        }

        .alert-content {
          margin: 0;
          font-size: 1.05rem;
          font-weight: 600;
          line-height: 1.6;
        }

        .alert-info { background: #EEF2FF; border-color: #4F46E5; }
        .alert-info .alert-header { color: #4F46E5; }
        .alert-info .alert-content { color: #312E81; }

        .alert-success { background: #ECFDF5; border-color: #10B981; }
        .alert-success .alert-header { color: #059669; }
        .alert-success .alert-content { color: #064E3B; }

        .alert-warning { background: #FFFBEB; border-color: #6366f1; }
        .alert-warning .alert-header { color: #D97706; }
        .alert-warning .alert-content { color: #78350F; }

        .alert-danger { background: #FEF2F2; border-color: #EF4444; }
        .alert-danger .alert-header { color: #DC2626; }
        .alert-danger .alert-content { color: #7F1D1D; }

        /* Mobile Controls */
        .mobile-toggle-btn {
          display: none;
          position: fixed;
          bottom: 30px;
          left: 30px;
          z-index: 50;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: #4F46E5;
          color: white;
          border: none;
          box-shadow: 0 10px 25px -5px rgba(79, 70, 229, 0.4);
          font-size: 1.5rem;
          cursor: pointer;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s;
        }

        .mobile-toggle-btn:active {
          transform: scale(0.9);
        }

        .sidebar-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.6);
          backdrop-filter: blur(4px);
          z-index: 30;
        }

        /* Lightbox */
        .lightbox-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.9);
          backdrop-filter: blur(8px);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: zoom-out;
        }

        .lightbox-image {
          max-width: 90vw;
          max-height: 90vh;
          object-fit: contain;
          border-radius: 12px;
          box-shadow: 0 25px 50px -12px rgba(0,0,0,0.5);
          cursor: default;
        }

        .lightbox-close {
          position: absolute;
          top: 30px;
          right: 30px;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: rgba(255,255,255,0.1);
          color: white;
          border: none;
          font-size: 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s;
        }
        
        .lightbox-close:hover {
          background: rgba(255,255,255,0.2);
        }

        @media (max-width: 1024px) {
          .docs-main { padding: 30px; }
          .docs-hero { padding: 40px 30px; }
          .docs-main-title { font-size: 2.2rem; }
        }

        @media (max-width: 768px) {
          .mobile-toggle-btn { display: flex; }
          .sidebar-overlay { display: block; }
          
          .lang-ar .docs-sidebar {
            position: fixed;
            right: 0;
            left: auto;
            top: 0;
            transform: translateX(100%);
          }
          
          .lang-en .docs-sidebar {
            position: fixed;
            left: 0;
            right: auto;
            top: 0;
            transform: translateX(-100%);
          }
          
          .docs-sidebar.open {
            transform: translateX(0) !important;
          }
          
          .lang-toggle-btn {
            position: relative;
            align-self: flex-start;
            margin-bottom: 20px;
          }

          .docs-main { padding: 20px 16px; }
          .docs-hero { margin-bottom: 30px; border-radius: 16px; }
          .docs-main-title { font-size: 1.8rem; }
          
          .section-card { padding: 24px 20px; }
          .section-heading h2 { font-size: 1.5rem; }
          
          .actions-timeline {
            border-right: none;
            padding-right: 0;
            margin-right: 0;
          }
          
          .action-block::before { display: none; }
          
          .action-card { padding: 20px; }
          .action-title { font-size: 1.2rem; }
        }
      `}</style>
    </div>
  );
};

export default TeacherDocumentation;
